import { Ref, SetStateAction } from 'react';
import { cn } from '@/lib/utils';
import { trpc } from '@/data-loaders';
import { useAwmsStore } from '@/store';
import { useHandleError, useHandleSuccess } from '@/hooks';
import { Column, ColumnDef, FilterFn, Row, Table } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@/components/Common/DataTable';
import { HdrDetails } from '#server/models/hdr';
import { Switch } from '@/components/ui';
import { formatHours, formatNumber } from '@/lib/utils';

export interface ExtendedHdrDetails extends HdrDetails {
  parentRef: Ref<HTMLDivElement>;
}

const calculateHdrHours = (row: Row<ExtendedHdrDetails>) => {
  let hdrHours = 0;
  if (row.original['hdrStudentWorkloadSupervisorRole'].toLocaleLowerCase() !== 'chairperson') {
    hdrHours =
      (row.original['hdrStudentWorkloadSupervisorPercentage'] / 100) *
      row.original['hdrStudentWorkloadEftsl'] *
      row.original['hdrSupervisorAllowance'];
  } else {
    hdrHours = row.original['hdrThesisChairAllowance'];
  }

  return hdrHours;
};

const createHeader = <TData,>(title: string, sortable: boolean) => {
  return ({ column, table }: { column: Column<TData, unknown>; table: Table<TData> }) => {
    let totalHdrHours = undefined;

    if (title === 'TRD HDR' || title === 'Research HDR') {
      // Determine the allocation type to filter by
      const allocationTypeFilter = title === 'TRD HDR' ? 'trd' : 'research';

      totalHdrHours = table.getRowModel().rows.reduce((total: number, row: Row<TData>) => {
        const hdrHours = calculateHdrHours(row as unknown as Row<ExtendedHdrDetails>);
        const allocationType = (row.original as ExtendedHdrDetails)[
          'hdrStudentWorkloadAllocationType'
        ].toLocaleLowerCase();
        return allocationType === allocationTypeFilter ? total + hdrHours : total;
      }, 0);
    }

    return (
      <DataTableColumnHeader
        column={column}
        title={title}
        sortable={sortable}
        titleClassName="text-secondary-grey-02 pl-0 pr-8"
        columnTotal={totalHdrHours}
      />
    );
  };
};

const createCell =
  (key: string, className?: string, setHasSavedHdr?: React.Dispatch<React.SetStateAction<boolean>>) =>
  ({ row }: { row: Row<ExtendedHdrDetails> }) => {
    const trpcUtils = trpc.useUtils();
    const selectedSchoolId = useAwmsStore((state) => state.selectedSchoolId);
    const selectedStaff = useAwmsStore((state) => state.selectedStaff);
    const selectedStaffId = selectedStaff?.id ?? 0;
    const handleError = useHandleError();
    const sendToastStaffUnit = useHandleSuccess({
      title: 'HDR allocation type updated',
    });
    const value = row.getValue(key);

    switch (key) {
      case 'student':
        return (
          <div className="flex w-[218px] items-center py-3 pr-[10px]">
            <div className="flex w-[218px] flex-col items-start justify-center">
              <div className="flex text-b1 font-semibold text-primary-black-90">
                {row.original['hdrStudentWorkloadStudentLastName']},{' '}
                {row.original['hdrStudentWorkloadStudentFirstName']}
              </div>
              <div className="flex text-b3 text-secondary-grey-02">
                {row.original['hdrStudentWorkloadStudentCurtinId']}
              </div>
              <div className="flex text-b3 text-secondary-grey-02">{row.original['udc']}</div>
            </div>
          </div>
        );

      case 'enrolment':
        return (
          <div className="flex w-[130px] items-center py-3 pr-[10px]">
            <div className="flex w-[150px] flex-col items-start justify-center">
              <div className="flex text-b2 font-semibold text-primary-black-90">
                Stage: {row.original['hdrStudentWorkloadEnrolmentStage']}
              </div>
              <div className="flex text-b3 text-primary-black-90">
                Status: {row.original['hdrStudentWorkloadEnrolmentStatus']}
              </div>
            </div>
          </div>
        );

      case 'hdrStudentWorkloadSupervisorPercentage':
        return (
          <div className="flex w-[130px] items-center py-3 text-b2 font-semibold text-primary-black-90">
            <>{row.original['hdrStudentWorkloadSupervisorPercentage']} %</>
          </div>
        );

      case 'hdrStudentWorkloadTrdHours':
      case 'hdrStudentWorkloadResearchHours': {
        const hdrHours = calculateHdrHours(row);
        const allocationType = row.original['hdrStudentWorkloadAllocationType'].toLocaleLowerCase();
        const shouldResetHdr =
          key === 'hdrStudentWorkloadTrdHours' ? allocationType !== 'trd' : allocationType !== 'research';
        return (
          <div
            className={`flex w-[130px] items-center py-3 text-b2 font-semibold text-primary-black-90
              ${shouldResetHdr ? 'text-secondary-grey-04' : ''}`}
          >
            {shouldResetHdr ? '0 hour' : formatHours(hdrHours)}
          </div>
        );
      }

      case 'hdrStudentWorkloadAllocationType': {
        const isResearch = row.original.hdrStudentWorkloadAllocationType.toLowerCase() === 'research';

        const { isLoading: isUpdating, mutate: updateHdrAllocationType } = trpc.hdr.updateHDRAllocationType.useMutation(
          {
            onSuccess: async () => {
              await trpcUtils.hdr.getHdrDetails.invalidate({ schoolId: selectedSchoolId, academicId: selectedStaffId });
              await trpcUtils.hdr.getHdrDetails.refetch();
              await trpcUtils.academic.getAcademicWorkloadAllocations.invalidate({
                schoolId: selectedSchoolId,
                academicId: selectedStaffId,
              });
              await trpcUtils.academic.getAcademicWorkloadAllocations.refetch({
                schoolId: selectedSchoolId,
                academicId: selectedStaffId,
              });
              if (setHasSavedHdr) {
                setHasSavedHdr(true);
              }
              sendToastStaffUnit();
            },
            onError: (error) => {
              console.error('Error update HDR allocation type', error);
              handleError([error]);
            },
          },
        );

        const handleToggle = async (value: boolean) => {
          // Update the allocation type based on the toggle value
          // toggle value = true => pointing to Research
          // toggle value = false => pointing to TRD
          const newValue = value ? 'Research' : 'TRD'; // Convert boolean back to correct value
          updateHdrAllocationType({
            hdrStudentWorkloadId: row.original.hdrStudentWorkloadId,
            allocationType: newValue,
          });
        };

        return (
          <div className="flex w-[75px] items-center py-3">
            <label
              className={`relative inline-block h-5 w-9 cursor-pointer items-center rounded-full ${isUpdating ? 'cursor-wait' : 'cursor-pointer'}`}
            >
              <Switch
                checked={isResearch}
                onCheckedChange={(value) => handleToggle(value)}
                className={`h-2 data-[state=checked]:bg-secondary-grey-04 data-[state=unchecked]:bg-secondary-grey-04 [&>span]:bg-deep-blue
                  ${isUpdating ? 'cursor-wait' : 'cursor-pointer'}`}
              />
            </label>
          </div>
        );
      }

      default:
        return (
          <div className={cn('text-primary-90 flex w-[130px] items-center py-3 text-b2 font-semibold', className)}>
            <>{typeof value === 'number' ? formatNumber(value) : value}</>
          </div>
        );
    }
  };

// columnId is needed in filterFn column option (https://tanstack.com/table/v8/docs/guide/column-filtering#filterfns)
const studentFilterFn: FilterFn<ExtendedHdrDetails> = (row, columnId, value) => {
  const student = `${row.original.hdrStudentWorkloadStudentCurtinId} ${row.original.hdrStudentWorkloadStudentFirstName}
      ${row.original.hdrStudentWorkloadStudentLastName}  ${row.original.udc}`.toLowerCase();
  return student.includes(value.toLowerCase());
};

export const hdrColumns = (setHasSavedHdr: Dispatch<SetStateAction<boolean>>): ColumnDef<ExtendedHdrDetails>[] => [
  {
    accessorKey: 'student',
    accessorFn: (row) =>
      `${row.hdrStudentWorkloadStudentCurtinId} ${row.hdrStudentWorkloadStudentFirstName} ${row.hdrStudentWorkloadStudentLastName} ${row.udc}`,
    filterFn: studentFilterFn,
    header: createHeader('Student', true),
    cell: createCell('student'),
  },
  {
    accessorKey: 'hdrStudentWorkloadEftsl',
    header: createHeader('EFTSL', false),
    cell: createCell('hdrStudentWorkloadEftsl'),
  },
  {
    accessorKey: 'enrolment',
    accessorFn: (row) => `${row.hdrStudentWorkloadEnrolmentStage} ${row.hdrStudentWorkloadEnrolmentStatus}`,
    header: createHeader('Enrolment', false),
    cell: createCell('enrolment'),
  },
  {
    accessorKey: 'hdrStudentWorkloadSupervisorRole',
    header: createHeader('Role', false),
    cell: createCell('hdrStudentWorkloadSupervisorRole'),
  },
  {
    accessorKey: 'hdrStudentWorkloadSupervisorPercentage',
    header: createHeader('Supervisor %', false),
    cell: createCell('hdrStudentWorkloadSupervisorPercentage'),
  },
  {
    accessorKey: 'hdrStudentWorkloadTrdHours',
    header: createHeader('TRD HDR', false),
    cell: createCell('hdrStudentWorkloadTrdHours'),
  },
  {
    accessorKey: 'hdrStudentWorkloadAllocationType',
    header: createHeader('', false),
    cell: createCell('hdrStudentWorkloadAllocationType', undefined, setHasSavedHdr),
  },
  {
    accessorKey: 'hdrStudentWorkloadResearchHours',
    header: createHeader('Research HDR', false),
    cell: createCell('hdrStudentWorkloadResearchHours'),
  },
];
